"use client";
import {
  fadeUp,
  slideLeft,
  slideRight,
  stagger,
} from "../components/motionVariants";
import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { Globe, Workflow, Users, ShieldCheck } from "lucide-react";

// ─── Data ─────────────────────────────────────────────────────────────────────

const coreValues = [
  {
    iconType: "india",
    title: "India-First, Not India-Adapted",
    desc: "GST, E-Invoice, E-Way Bill, and GSTIN auto-fill - built into the product from day one, not added as a compliance module years later. Every Indian tax rule is a first-class citizen in ABSS.",
    iconBg: "bg-orange-50/70 border-orange-100/80 text-[#FB923C]",
  },
  {
    iconType: "cycle",
    title: "Full Cycle, Not Just a Module",
    desc: "A lead in CRM, a quote, a sales order, a delivery challan, an invoice, and a payment received - all connected in one workflow. No stitching between separate tools, no manual data re-entry between systems.",
    iconBg: "bg-emerald-50/70 border-emerald-100/80 text-[#4ADE80]",
  },
  {
    iconType: "sme",
    title: "Right-Sized for SMEs",
    desc: "No 6-month implementation. No dedicated IT team required. No per-module licensing surprises. An owner can set up ABSS Books in a day and start issuing GST-compliant invoices before lunch.",
    iconBg: "bg-purple-50/70 border-purple-100/80 text-purple-600",
  },
  {
    iconType: "reliability",
    title: "Built for Reliability",
    desc: "SME owners can't afford downtime during GST filing season or month-end close. We build ABSS for uptime, performance, and data integrity first - features come second to stability.",
    iconBg: "bg-blue-50/70 border-blue-100/80 text-blue-600",
  },
];

function CoreValueIcon({ type }) {
  const iconClass = "w-5 h-5";
  switch (type) {
    case "india":
      return <Globe className={iconClass} strokeWidth={2} />;
    case "cycle":
      return <Workflow className={iconClass} strokeWidth={2} />;
    case "sme":
      return <Users className={iconClass} strokeWidth={2} />;
    case "reliability":
      return <ShieldCheck className={iconClass} strokeWidth={2} />;
    default:
      return null;
  }
}

const companyValues = [
  {
    title: "Customer-Driven Roadmap",
    text: "Every ABSS feature started with a real customer problem. When manufacturers asked for QR code lead capture at trade shows, we built it. Our backlog is a list of real pain points, not a SaaS feature checklist.",
  },
  {
    title: "Transparent Pricing",
    text: "You see exactly what you get at each plan tier, with prices in ₹ and GST shown separately. No hidden charges, nothing buried in fine print.",
  },
  {
    title: "Priced for Access",
    text: "Enterprise software priced out of reach doesn't help Indian SMEs. We keep our pricing honest so a business with 10 employees can afford the same core tools as a business with 500.",
  },
  {
    title: "Long-Term Partnership",
    text: "We're not building a product to flip - we're building infrastructure for Indian businesses to run on for decades. That means stability in our codebase, our pricing, and our support commitments.",
  },
  {
    title: "Speed Without Sacrifice",
    text: "We ship fast because Indian businesses evolve fast. New GST rules, new compliance requirements, new workflows - we prioritise critical compliance updates so you're never left behind.",
  },
  {
    title: "Honest About Limitations",
    text: "ABSS is not the right fit for every business. If your workflow needs something we don't do well yet, we'll tell you - and add it to the roadmap rather than oversell what exists today.",
  },
];

const problemStats = [
  {
    number: "63M+",
    label: "SMEs in India",
    sub: "most still managing accounts in desktop software + spreadsheets",
  },
  {
    number: "₹20L Cr+",
    label: "GST collected annually",
    sub: "but most businesses still re-enter data manually",
  },
  {
    number: "40%",
    label: "of SMEs use 3+ tools",
    sub: "CRM, accounting, inventory - all disconnected",
  },
  {
    number: "2–3 hrs",
    label: "wasted daily",
    sub: "on reconciliation, re-entry, and manual GST prep",
  },
];

const milestones = [
  {
    year: "Apr 2024",
    title: "Company Founded",
    desc: "ABSS was incorporated with a vision to simplify accounting, inventory, GST compliance, and business operations for Indian SMEs through one connected platform.",
  },
  {
    year: "May – Aug 2024",
    title: "Research & Customer Discovery",
    desc: "We met with accountants, manufacturers, traders, distributors, and business owners to understand their day-to-day challenges. Instead of following market trends, we focused on solving real business problems.",
  },
  {
    year: "Sep – Dec 2024",
    title: "Product Planning & Architecture",
    desc: "The foundation of ABSS was designed around a single source of truth—bringing accounting, inventory, CRM, production, and compliance together in one unified platform.",
  },
  {
    year: "Jan – Jun 2025",
    title: "Core Development Begins",
    desc: "Development started on the accounting engine, GST workflows, inventory management, sales, purchases, banking, reporting, and CRM. Every module was designed to work seamlessly together.",
  },
  {
    year: "Jul – Dec 2025",
    title: "Expanding the Platform",
    desc: "Production management, advanced reporting, dashboards, analytics, and automation features were added, making ABSS a complete ERP solution for growing businesses.",
  },
  {
    year: "Jan – Jun 2026",
    title: "Testing with Real Businesses",
    desc: "Pilot customers started using ABSS in their daily operations. Their feedback helped us improve workflows, enhance usability, optimize performance, and fine-tune business processes before release.",
  },
  {
    year: "Aug 2026",
    title: "Version 1.0 Completed",
    highlight: true,
    desc: "After more than two years of research, design, development, and testing, ABSS Version 1.0 was completed. The platform now delivers accounting, GST, inventory, CRM, production, reporting, and business insights in one integrated solution for Indian businesses.",
  },
];

const whatWeBuilt = [
  {
    number: "12+",
    label: "Report types",
    sub: "General Ledger, P&L, Balance Sheet, aging, item-wise, customer-wise",
  },
  {
    number: "100%",
    label: "Cloud-based",
    sub: "Access from any browser, plus iOS and Android apps",
  },
  {
    number: "2",
    label: "Core products",
    sub: "ABSS Books and ABSS CRM - fully integrated, shared data layer",
  },
  {
    number: "3",
    label: "Recurring document types",
    sub: "Invoices, bills, and expenses created automatically on schedule",
  },
];

const sectionPadding = "px-5 sm:px-6 md:px-10 lg:px-12";

// ─── Component ────────────────────────────────────────────────────────────────

export default function About() {
  const router = useRouter();

  return (
    <div className="overflow-x-hidden w-full min-h-screen bg-white">
      {/* ── Hero ── */}
      <section className="relative w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <div className="absolute top-[-25%] left-[-15%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-orange-200/40 to-amber-100/10 blur-[130px] animate-aurora-pulse" />
          <div className="absolute bottom-[-15%] right-[-10%] w-[55%] h-[75%] rounded-full bg-gradient-to-tr from-blue-200/30 to-indigo-100/10 blur-[130px] animate-aurora-pulse [animation-delay:3s]" />
          <div className="absolute inset-0 bg-grid-mesh opacity-80" />
        </div>

        <motion.div
          className={`relative z-10 max-w-[1160px] mx-auto pt-24 sm:pt-28 md:pt-32 pb-10 sm:pb-16 ${sectionPadding} min-w-0`}
          initial="hidden"
          animate="show"
          variants={stagger}
        >
          <motion.div
            variants={fadeUp}
            className="max-w-[760px] mx-auto text-center mb-8 sm:mb-14 px-1"
          >
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-1.5 border border-[#e5e5e5] rounded-full px-3.5 sm:px-4 py-1.5 text-[12px] sm:text-[13px] text-[#262626] geist-semibold bg-white/80 backdrop-blur-sm shadow-sm mb-5 sm:mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-[#DF5D01] inline-block animate-pulse"></span>
              Our Story
            </motion.span>

            <h1 className="text-[26px] sm:text-[48px] md:text-[60px] leading-[1.15] sm:leading-[1.12] text-[#171717] mb-4 sm:mb-6 sentient-bold tracking-tight">
              Built in India,
              <br className="hidden sm:block" /> for India&apos;s Entrepreneurs
            </h1>
            <p className="text-[15px] sm:text-lg md:text-[21px] font-medium leading-relaxed text-[#262626] max-w-[640px] mx-auto geist-medium">
              ABSS was created because India&apos;s SMEs deserved software that
              understands GST from day one, handles Indian sales workflows
              natively, and doesn&apos;t require a 6-month implementation or a
              dedicated IT team to run.
            </p>
          </motion.div>

          {/* Story Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 bg-gradient-to-br from-white to-[#FAF9F8] border border-[#e5e5e5] rounded-xl sm:rounded-3xl overflow-hidden shadow-md sm:shadow-lg min-w-0">
            <motion.div variants={slideRight} className="order-1 md:order-2 min-w-0 border-b md:border-b-0 md:border-l border-[#e5e5e5]">
              <img
                src="/images/about-our-story-vertical.jpg"
                alt="Transitioning Indian Small Businesses from Paper to Cloud"
                className="w-full h-[320px] md:h-full object-cover"
              />
            </motion.div>

            <motion.div
              variants={slideLeft}
              className="order-2 md:order-1 p-5 sm:p-10 md:p-14 flex flex-col justify-center text-center md:text-left min-w-0"
            >
              <span className="inline-flex items-center gap-1.5 border border-orange-100 rounded-full px-3 sm:px-3.5 py-1.5 text-[11px] geist-semibold bg-orange-50/40 text-orange-700 shadow-sm mb-4 sm:mb-5 self-center md:self-start">
                <span className="w-1.5 h-1.5 rounded-full bg-[#DF5D01] inline-block animate-pulse"></span>
                Our Story
              </span>
              <h3 className="text-[20px] sm:text-[28px] md:text-[34px] leading-[1.25] text-[#171717] sentient-medium mb-4 sm:mb-5 tracking-tight">
                Why we built ABSS
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-[#333333] text-[13px] sm:text-[15px] leading-relaxed geist-medium">
                  India has over 63 million small and medium businesses. Most of
                  them manage accounts in desktop software, run their CRM in spreadsheets,
                  and prepare GST returns using a CA&apos;s offline tool. Three
                  separate systems, none of which talk to each other - meaning
                  data gets entered two or three times, errors compound, and
                  nobody has a single view of the business.
                </p>
                <p className="text-[#333333] text-[13px] sm:text-[15px] leading-relaxed geist-medium">
                  We built ABSS because we kept seeing the same problems:
                  manufacturers tracking production on paper, distributors
                  reconciling invoices manually every morning, traders re-entering
                  invoice data into the GST portal at month end. Each problem had
                  a software solution somewhere - but no single platform solved
                  all of them for an Indian SME at an accessible price.
                </p>
                <p className="text-[#333333] text-[13px] sm:text-[15px] leading-relaxed geist-medium">
                  ABSS is that platform. Starting with ABSS Books for accounting
                  and ABSS CRM for sales, we&apos;re building the complete
                  operating system for Indian businesses - one real customer
                  workflow at a time.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ── The Problem We Solve ── */}
      <section className="w-full bg-[#f5f5f5] border-t border-b border-[#E5E5E5] overflow-hidden">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          <motion.p
            variants={fadeUp}
            className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3 text-center md:text-left"
          >
            The Problem
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="text-[22px] sm:text-[36px] md:text-[44px] leading-[1.25] sm:leading-[1.2] text-[#171717] sentient-medium mb-3 sm:mb-4 max-w-[700px] mx-auto md:mx-0 text-center md:text-left"
          >
            Indian SMEs are running on tools that weren&apos;t built for them
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-[13px] sm:text-[17px] text-[#333333] geist-medium leading-[1.65] sm:leading-[1.7] mb-8 sm:mb-12 max-w-[640px] mx-auto md:mx-0 text-center md:text-left"
          >
            Western accounting software gets &ldquo;localized&rdquo; with a GST
            plugin. CRM tools built for enterprise sales teams get sold to
            10-person trading companies. The result is expensive, complicated
            software that SMEs use at 20% of its capability - or abandon
            entirely.
          </motion.p>

          {/* Stats Grid */}
          <motion.div
            variants={stagger}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8 min-w-0"
          >
            {problemStats.map((stat, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="bg-white border border-[#E5E5E5] rounded-xl sm:rounded-2xl p-3.5 sm:p-6 text-left min-w-0 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <p className="text-[22px] sm:text-[36px] text-[#171717] sentient-medium leading-none mb-1.5 sm:mb-2">
                  {stat.number}
                </p>
                <p className="text-[12px] sm:text-[15px] text-[#171717] sentient-medium mb-1">
                  {stat.label}
                </p>
                <p className="text-[10px] sm:text-[12px] text-[#737373] geist-medium leading-[1.5]">
                  {stat.sub}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* ── Mission ── */}
      <motion.section
        className={`relative w-full bg-white border-b border-[#E5E5E5] max-w-[1160px] mx-auto py-10 sm:py-16 md:py-24 ${sectionPadding} text-center overflow-hidden min-w-0`}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={stagger}
      >
        <motion.p
          variants={fadeUp}
          className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-4"
        >
          Our Mission
        </motion.p>
        <motion.h2
          variants={fadeUp}
          className="text-[#171717] text-[22px] sm:text-[30px] md:text-[38px] lg:text-[44px] leading-[1.25] sm:leading-[1.2] sentient-medium max-w-[900px] mx-auto mb-6 sm:mb-8 px-1 tracking-tight"
        >
          Empower every Indian SME with enterprise-grade software - accounting,
          CRM, inventory, and compliance - at a price and complexity that works
          for a business of 5, not 500.
        </motion.h2>

        {/* Mission pillars */}
        <motion.div
          variants={stagger}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mt-8 sm:mt-10 max-w-[880px] mx-auto min-w-0"
        >
          {[
            {
              label: "Accessible",
              desc: "Priced for SMEs, not just enterprises. Compliance is built into the product - not sold as a separate plugin.",
            },
            {
              label: "Integrated",
              desc: "CRM and Books on one platform. One dataset. No exports, no re-entry, no reconciliation.",
            },
            {
              label: "India-Native",
              desc: "GST, E-Invoice, E-Way Bill built in from the start - not added as afterthoughts.",
            },
          ].map((p, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="bg-white border border-[#E5E5E5] rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center sm:text-left min-w-0 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <h4 className="text-[16px] sm:text-[18px] text-[#171717] sentient-medium mb-2">
                {p.label}
              </h4>
              <p className="text-[12px] sm:text-[13px] text-[#333333] geist-medium leading-relaxed">
                {p.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      {/* ── Core Values ── */}
      <motion.section
        className="w-full bg-white border-b border-[#E5E5E5] overflow-hidden"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={stagger}
      >
        <div className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}>
          <motion.p
            variants={fadeUp}
            className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3 text-center md:text-left"
          >
            Core Values
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="text-[22px] sm:text-[36px] md:text-[44px] text-[#171717] mb-3 sm:mb-4 sentient-medium text-center md:text-left"
          >
            What we believe about building software for Indian businesses
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-[13px] sm:text-[17px] text-[#333333] mb-8 sm:mb-12 geist-medium leading-relaxed max-w-[640px] mx-auto md:mx-0 text-center md:text-left"
          >
            These aren&apos;t values we put on a wall - they&apos;re the
            decisions we make every day about what to build, how to price it,
            and who we build it for.
          </motion.p>

          <motion.div
            variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-12 min-w-0"
          >
            {coreValues.map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="group flex flex-col items-center md:items-start text-center md:text-left gap-3 sm:gap-5 border border-neutral-100/80 sm:border-transparent hover:border-neutral-200/50 hover:bg-neutral-50/40 p-4 sm:p-6 rounded-xl sm:rounded-2xl transition-all duration-300 sm:-m-6 min-w-0"
              >
                <div
                  className={`flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-xl border ${item.iconBg} shadow-sm transition-transform duration-300 group-hover:scale-105 shrink-0`}
                >
                  <CoreValueIcon type={item.iconType} />
                </div>
                <div className="min-w-0 w-full">
                  <h3 className="text-[18px] sm:text-[22px] md:text-[24px] text-[#171717] mb-1.5 sm:mb-2 sentient-medium">
                    {item.title}
                  </h3>
                  <p className="text-[#333333] text-[13px] sm:text-[14px] md:text-[15px] leading-relaxed geist-medium max-w-[480px] mx-auto md:mx-0">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* ── What We&apos;ve Built - Timeline ── */}
      <section className="w-full bg-[#f5f5f5] border-b border-[#E5E5E5] overflow-hidden">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={stagger}
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          <motion.p
            variants={fadeUp}
            className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3 text-center md:text-left"
          >
            Product Timeline
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="text-[22px] sm:text-[36px] md:text-[44px] text-[#171717] sentient-medium mb-3 sm:mb-4 text-center md:text-left"
          >
            How ABSS came to life
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-[13px] sm:text-[17px] text-[#333333] geist-medium leading-[1.65] sm:leading-[1.7] mb-8 sm:mb-12 max-w-[640px] mx-auto md:mx-0 text-center md:text-left"
          >
            From a bold idea in 2024 to a complete accounting platform in 2026 —
            every milestone reflects countless conversations with Indian
            businesses and a commitment to solving real operational challenges.
          </motion.p>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line - desktop */}
            <div className="hidden md:block absolute left-[150px] top-0 bottom-0 w-px bg-[#E5E5E5]"></div>

            <div className="space-y-8 sm:space-y-10">
              {milestones.map((m, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="flex flex-col md:flex-row gap-3 md:gap-0"
                >
                  {/* Year */}
                  <div className="md:w-[150px] flex-shrink-0 flex md:justify-end md:pr-6 items-start md:pt-1">
                    <span
                      className={`text-[12px] sm:text-[13px] font-medium geist-medium px-2.5 py-1 rounded-full border text-center leading-tight md:text-right ${m.highlight ? "bg-neutral-900 border-neutral-950 text-white" : "bg-white border-[#E5E5E5] text-[#333333]"}`}
                    >
                      {m.year}
                    </span>
                  </div>

                  {/* Dot - desktop */}
                  <div className="hidden md:flex items-start pt-2 px-4">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-0.5 bg-[#DF5D01]"></div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-white border border-[#E5E5E5] rounded-xl sm:rounded-2xl p-4 sm:p-6 md:ml-2 min-w-0 shadow-sm hover:shadow-md transition-all duration-300">
                    <h4 className="text-[14px] sm:text-[17px] text-[#171717] sentient-medium mb-1.5">
                      {m.title}
                      {m.highlight && (
                        <span className="ml-0 sm:ml-2 mt-1 sm:mt-0 inline-block text-[10px] sm:text-[11px] text-[#DF5D01] geist-medium border border-orange-200 bg-orange-50 px-2 py-0.5 rounded-full">
                          Released
                        </span>
                      )}
                    </h4>
                    <p className="text-[12px] sm:text-[13px] text-[#333333] geist-medium leading-[1.65]">
                      {m.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* What's Next */}
          <motion.div
            variants={fadeUp}
            className="mt-10 sm:mt-14 bg-neutral-900 text-white rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-12"
          >
            <p className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3">
              What&apos;s Next
            </p>
            <h3 className="text-[20px] sm:text-[28px] md:text-[32px] sentient-medium mb-3 sm:mb-4">
              The Journey Continues
            </h3>
            <p className="text-[13px] sm:text-[15px] md:text-[16px] text-neutral-300 geist-medium leading-[1.7] sm:leading-[1.75] max-w-[760px]">
              This is only the beginning. Future releases will introduce
              AI-powered automation, deeper business intelligence, team
              collaboration, warehouse management, logistics, and many more
              capabilities—driven by customer feedback, just as ABSS has been
              from day one.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* ── What We&apos;ve Built - Numbers ── */}
      <section className="w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 md:gap-16 items-center min-w-0">
            <motion.div
              variants={slideLeft}
              className="text-center md:text-left flex flex-col items-center md:items-start min-w-0"
            >
              <p className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3">
                The Platform
              </p>
              <h2 className="text-[22px] sm:text-[36px] md:text-[42px] leading-[1.25] text-[#171717] sentient-medium mb-4 sm:mb-5">
                What we&apos;ve shipped for Indian businesses
              </h2>
              <p className="text-[13px] sm:text-[16px] text-[#333333] geist-medium leading-[1.65] sm:leading-[1.75] mb-5 sm:mb-6 max-w-[520px]">
                ABSS isn&apos;t a single-module tool. It&apos;s a full business
                operating system - built module by module, each one solving a
                specific workflow that Indian SMEs face daily. Here&apos;s the
                scope of what&apos;s already available.
              </p>
              <button
                onClick={() => router.push("/features/books")}
                className="inline-flex items-center justify-center border border-[#E5E5E5] h-[46px] text-[#262626] px-6 rounded-lg hover:bg-neutral-50 transition-all duration-300 geist-semibold text-sm w-full sm:w-auto max-w-[320px] sm:max-w-none shadow-sm"
              >
                Explore all features →
              </button>
            </motion.div>

            <motion.div
              variants={slideRight}
              className="grid grid-cols-2 gap-3 sm:gap-5 min-w-0 w-full max-w-[480px] mx-auto md:max-w-none md:mx-0"
            >
              {whatWeBuilt.map((stat, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white border border-[#E5E5E5] rounded-xl sm:rounded-2xl p-3.5 sm:p-6 min-w-0 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <p className="text-[24px] sm:text-[38px] text-[#171717] sentient-medium leading-none mb-1.5 sm:mb-2">
                    {stat.number}
                  </p>
                  <p className="text-[12px] sm:text-[15px] text-[#171717] sentient-medium mb-1">
                    {stat.label}
                  </p>
                  <p className="text-[10px] sm:text-[12px] text-[#737373] geist-medium leading-[1.5]">
                    {stat.sub}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ── Who We Are ── */}
      <motion.section
        className="relative w-full bg-[#f5f5f5] py-10 sm:py-16 md:py-24 overflow-hidden border-b border-[#E5E5E5]"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        variants={stagger}
      >
        <div className={`relative max-w-[1160px] mx-auto ${sectionPadding} text-center min-w-0`}>
          <motion.p
            variants={fadeUp}
            className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3"
          >
            Who We Are
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="text-[#171717] text-[22px] sm:text-[32px] md:text-[40px] leading-[1.35] sm:leading-[1.4] sentient-medium mx-auto mb-4 sm:mb-5 max-w-[740px] px-1"
          >
            Engineers and operators who&apos;ve worked with Indian SMEs
            firsthand
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-[#333333] text-[13px] sm:text-[16px] geist-medium leading-[1.65] sm:leading-[1.7] max-w-[620px] mx-auto mb-8 sm:mb-14 px-1"
          >
            We&apos;ve sat with manufacturers in Surat, distributors in Chennai,
            and service businesses in Bengaluru. We watched how they actually
            work - not how they describe their work in a survey. ABSS was built
            from those conversations, and every new feature goes through the
            same process.
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="overflow-hidden rounded-xl sm:rounded-[16px] px-1"
          >
            <motion.img
              src="/images/about-team-collaboration.jpg"
              alt="The ABSS Team collaborating on software features"
              className="w-full max-w-[1080px] h-auto rounded-xl sm:rounded-[16px] mx-auto"
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true }}
            />
          </motion.div>
        </div>
      </motion.section>

      {/* ── Company Values - Scrolling ── */}
      <motion.section
        className={`w-full bg-white border-b border-[#E5E5E5] max-w-[1160px] mx-auto py-10 sm:py-16 md:py-20 ${sectionPadding} overflow-hidden min-w-0`}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        variants={stagger}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 items-center min-w-0">
          {/* Left */}
          <motion.div
            variants={fadeUp}
            className="text-center md:text-left space-y-4 sm:space-y-5 flex flex-col items-center md:items-start min-w-0"
          >
            <p className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium">
              How We Operate
            </p>
            <h2 className="text-[22px] sm:text-[36px] md:text-[42px] leading-[1.3] text-[#171717] sentient-medium">
              Built on Trust.
              <br />
              Focused on Impact.
            </h2>
            <p className="text-[#333333] text-[13px] sm:text-[15px] leading-[1.65] sm:leading-[1.75] geist-medium max-w-[400px]">
              These principles guide every product decision, every pricing call,
              and every support interaction we have with ABSS customers.
            </p>
          </motion.div>

          {/* Right Scrolling Cards */}
          <motion.div
            variants={fadeUp}
            className="relative w-full max-w-[510px] h-[280px] sm:h-[420px] mx-auto overflow-hidden scroll-fade-mask min-w-0"
          >
            <div className="animate-scroll-up flex flex-col space-y-4">
              {[...Array(2)].map((_, group) => (
                <div key={group} className="flex flex-col space-y-4">
                  {companyValues.map((value, i) => (
                    <motion.div
                      key={i}
                      whileHover={{
                        scale: 1.01,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                      }}
                      className="w-full flex flex-col gap-2 px-4 sm:px-5 py-4 border border-[#E5E5E5] rounded-xl sm:rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      <h3 className="text-[15px] sm:text-[17px] text-[#171717] sentient-medium">
                        {value.title}
                      </h3>
                      <p className="text-[#333333] text-[12px] sm:text-[13px] leading-[1.65] geist-medium">
                        {value.text}
                      </p>
                    </motion.div>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <style jsx>{`
          @keyframes scroll-up {
            0% {
              transform: translateY(0);
            }
            100% {
              transform: translateY(-50%);
            }
          }
          .animate-scroll-up {
            animation: scroll-up 22s linear infinite;
            will-change: transform;
          }
        `}</style>
      </motion.section>

      {/* ── India Focus ── */}
      <section className="w-full bg-[#f5f5f5] border-b border-[#E5E5E5] overflow-hidden">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 md:gap-16 items-center min-w-0">
            <motion.div
              variants={slideLeft}
              className="space-y-4 sm:space-y-5 text-center md:text-left flex flex-col items-center md:items-start min-w-0"
            >
              <p className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium">
                India First
              </p>
              <h2 className="text-[22px] sm:text-[36px] md:text-[40px] leading-[1.25] text-[#171717] sentient-medium max-w-[520px]">
                Every Indian compliance requirement is a first-class feature
              </h2>
              <p className="text-[13px] sm:text-[16px] text-[#333333] geist-medium leading-[1.65] sm:leading-[1.75] max-w-[540px]">
                Most global SaaS tools treat GST as an add-on. We treat it as
                core infrastructure. Every invoice generated in ABSS is
                GST-structured from the start - with the right HSN codes, tax
                rates, GSTIN validation, and IGST/CGST/SGST splitting built in.
              </p>
              <p className="text-[13px] sm:text-[16px] text-[#333333] geist-medium leading-[1.65] sm:leading-[1.75] max-w-[540px]">
                When the government changes E-Invoice thresholds or updates
                E-Way Bill rules, we push compliance updates to all ABSS
                customers - no manual update, no plugin to install, no CA to
                call.
              </p>
            </motion.div>

            <motion.div variants={slideRight} className="space-y-3 sm:space-y-4 min-w-0 w-full">
              {[
                {
                  label: "GSTIN Lookup",
                  desc: "Enter a GST number - ABSS auto-fills company name, state, and registration status from the GST portal in real time.",
                },
                {
                  label: "E-Way Bill Generation",
                  desc: "Generate E-Way Bills from invoices or delivery challans for goods above ₹50,000 - document details auto-filled, vehicle and transporter details added at dispatch.",
                },
                {
                  label: "E-Invoice (IRN)",
                  desc: "For businesses above the e-invoicing threshold, ABSS generates the Invoice Reference Number directly. No separate IRP tool.",
                },
                {
                  label: "HSN / SAC & Tax Split",
                  desc: "HSN and SAC codes flow from your item master into every invoice, and ABSS applies the right IGST or CGST + SGST split automatically.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="flex items-start gap-3 sm:gap-4 bg-white border border-[#E5E5E5] rounded-xl sm:rounded-2xl p-3.5 sm:p-5 min-w-0 shadow-sm hover:shadow-md hover:border-neutral-300/80 transition-all duration-300"
                >
                  <div className="w-8 h-8 rounded-[8px] bg-neutral-50 border border-neutral-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                      <path
                        d="M1.5 5L5.5 9L12.5 1"
                        stroke="#171717"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-[14px] sm:text-[15px] text-[#171717] sentient-medium mb-1">
                      {item.label}
                    </h4>
                    <p className="text-[12px] sm:text-[13px] text-[#333333] geist-medium leading-[1.6]">
                      {item.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
